/**
 * Wire types shared by the Wellformed server and web client.
 *
 * Every user-facing table is a real SQL table; every column here is either a
 * real SQL column (`stored`) or an expression compiled into the SELECT list
 * (`formula` / `rollup`). Nothing in this file is invented at render time.
 */

import type { Locale } from './locale.js';
import type { PlanId } from './plans.js';

export const COLUMN_TYPES = [
  'text',
  'number',
  'currency',
  'date',
  'checkbox',
  'select',
  'link',
  'formula',
  'rollup',
] as const;

export type ColumnType = (typeof COLUMN_TYPES)[number];

/** Column kinds backed by a physical SQL column. */
export const STORED_COLUMN_TYPES = [
  'text',
  'number',
  'currency',
  'date',
  'checkbox',
  'select',
  'link',
] as const;

export type StoredColumnType = (typeof STORED_COLUMN_TYPES)[number];

export type ColumnFormat = 'currency' | 'number' | 'percent' | 'date' | 'text';

export function isComputed(type: ColumnType): boolean {
  return type === 'formula' || type === 'rollup';
}

export interface ColumnMeta {
  /**
   * The SQL identifier. Derived from the label at creation — see
   * `slugifyColumnName` — and fixed after it: formulas, filters, views and the
   * wire all address the column by this, and nobody is asked to spell it.
   */
  name: string;
  /** What people call the column: the grid header, in their own words. */
  label: string;
  type: ColumnType;
  position: number;
  /** `select` only. */
  options?: string[];
  /** `link` only — the table this foreign key points at. */
  target?: string;
  /** `formula` / `rollup` only. */
  expr?: string;
  format?: ColumnFormat | null;
  required?: boolean;
  /**
   * Whether no two rows may hold the same value. Backed by a real
   * `CREATE UNIQUE INDEX`, so it is the database refusing the second one, not
   * the server remembering to check.
   */
  unique?: boolean;
  /**
   * `text` only — the numbering pattern that fills this column when a row
   * arrives with the cell empty, e.g. `INV-{yyyy}-{nnn}`. The counter is read
   * off the rows already there, scoped to the pattern's rendered date parts,
   * so a pattern carrying {yyyy} starts over each year. See `sequence.ts`.
   */
  sequence?: string;
  /** Set when a stored `formula`/`rollup` expression no longer compiles. */
  error?: string | null;
}

/**
 * Tightening a column: the constraints being proposed for it.
 *
 * A spreadsheet arrives with the loosest schema a table can have — one bad cell
 * in a thousand makes the whole column text — and this is how it gets tightened
 * afterwards, one constraint at a time, the way `ALTER TABLE` would. Every field
 * is optional and absent means "leave it as it is".
 */
export interface Tightening {
  /** Convert the stored column to this type. */
  type?: StoredColumnType;
  /** The options, when the column is becoming (or staying) a select. */
  options?: string[];
  /** The table it points at, when it is becoming a link. */
  target?: string;
  required?: boolean;
  unique?: boolean;
  /** How to read the text that is in the column now, while converting it. */
  reading?: Reading;
  /** What to do with values that will not convert. Defaults to stopping. */
  onProblem?: 'stop' | 'clear';
}

/**
 * How ambiguous text is to be read on the way in.
 *
 * Both questions have no right answer from the value alone — `1.234` is a
 * thousand in Amsterdam and one-point-two in Chicago, `03/04` is April in one
 * and March in the other — so the plan guesses from the whole column, says
 * which way it guessed, and lets the answer be overruled.
 */
export interface Reading {
  /** Which mark separates the decimals. */
  decimal?: '.' | ',';
  /** Whether `03/04/2026` is 3 April rather than 4 March. */
  dayFirst?: boolean;
}

export type TightenProblemKind =
  | 'unreadable'
  | 'blank'
  | 'duplicate'
  | 'unknown_option'
  | 'no_match';

/** One reason a set of rows does not fit the proposed constraint. */
export interface TightenProblem {
  kind: TightenProblemKind;
  message: string;
  count: number;
  /** A few of the offenders, so the answer is rows rather than a number. */
  samples: { id: string; label: string; value: string }[];
  /** Whether emptying the cell would settle it. Duplicates and blanks never can. */
  clearable: boolean;
}

/**
 * What tightening a column would do, worked out against the data that is
 * actually in it — a dry run, in the sense `ALTER TABLE` has never offered one.
 */
export interface TightenPlan {
  /** Rows in the table. */
  total: number;
  /** Rows that satisfy the whole proposal as it stands. */
  fit: number;
  problems: TightenProblem[];
  /** Values the conversion would rewrite, sampled: `€ 1.234,50` → `1234.5`. */
  rewrites: { from: string; to: string }[];
  /** The distinct values, when there are few enough to become select options. */
  distinct: { value: string; count: number }[] | null;
  /** How the text was read, so the other reading can be asked for. */
  reading: Required<Reading>;
  /** True when at least one date could honestly be read either way round. */
  ambiguousDates: boolean;
  /** The statements this would run, for the same reason the grid shows its SELECT. */
  sql: string;
}

/**
 * Normalising a column: merging spellings that are the same answer.
 *
 * `c` beside 2,294 rows of `C`, `betaald` beside `Betaald`, a value wearing a
 * stray space — none of it is wrong enough to block a tightening, so it
 * survives every import and quietly ruins filters and option lists. The scan
 * finds these clusters; the merge rewrites the also-rans to the surviving
 * spelling. Both speak in exact values: nothing here is fuzzy matching, only
 * case and whitespace, which is why a merge can be offered as a guess.
 */
export interface NormalizeVariant {
  /** The spelling, whitespace already straightened — stray spaces are folded in silently. */
  value: string;
  /** Rows holding this spelling. A select option nobody picked counts 0. */
  count: number;
}

/** One set of spellings that read as the same answer. */
export interface NormalizeCluster {
  /** The spelling proposed to survive: the majority variant, trimmed. */
  to: string;
  /** Every spelling found, most rows first. */
  variants: NormalizeVariant[];
}

export interface NormalizeColumnPlan {
  column: string;
  clusters: NormalizeCluster[];
  /** Clusters found beyond the reported cap. */
  more: number;
}

export interface NormalizePlan {
  /** Only the columns where something clustered; a clean column says nothing. */
  columns: NormalizeColumnPlan[];
  /** How many text and select columns the scan read. */
  scanned: number;
}

/**
 * One merge to carry out: rewrite every cell spelling a `from` to `to`.
 * Spellings are matched with their whitespace straightened, and applying to a
 * column also straightens its stray whitespace — that part is automatic,
 * because spaces are not the kind of mess worth a question.
 */
export interface NormalizeMerge {
  to: string;
  from: string[];
}

/**
 * The tighten walk: every stored column of a table looked at once, each with
 * what its values say it really is. This is the guided face of tightening —
 * the operations were always there, this is the regie: walk the columns,
 * detect a datatype, merge the spellings where the column is an enum in
 * waiting, and offer the tightening that finishes it.
 */
export interface TightenWalkStep {
  column: string;
  /** The stored type every present value reads as, when tighter than text. */
  convert: StoredColumnType | null;
  /** A few of the values behind a `convert`, so the claim can be eyeballed. */
  samples: string[];
  /** Spelling groups to merge first — the normalising, where it fits. */
  clusters: NormalizeCluster[];
  /** The values as they stand after the merges: few and repeating enough to be a select's options. */
  select: NormalizeVariant[] | null;
  /** Rows holding a value, and rows in the table. */
  present: number;
  total: number;
}

export interface TightenWalkPlan {
  /** Only the columns with something to offer; a settled column says nothing. */
  steps: TightenWalkStep[];
  /** Stored, non-computed columns the walk looked at. */
  scanned: number;
}

export interface TableMeta {
  name: string;
  label: string;
  /** Column whose value labels a row in link chips, audit lines and pickers. */
  display: string;
  /**
   * The heading this table is listed under in the sidebar, or null for none.
   * Purely a way of shelving the list — nothing about the SQL namespace
   * changes, which is what separates this from a Postgres schema.
   */
  group: string | null;
  position: number;
  columns: ColumnMeta[];
  rowCount: number;
}

export const FILTER_OPS = [
  '=',
  '≠',
  '>',
  '<',
  '≥',
  '≤',
  'contains',
  'is empty',
  'is not empty',
] as const;

export type FilterOp = (typeof FILTER_OPS)[number];

/** Ops that ignore the `val` field entirely. */
export function isUnaryOp(op: FilterOp): boolean {
  return op === 'is empty' || op === 'is not empty';
}

export interface Filter {
  col: string;
  op: FilterOp;
  val: string;
}

export interface Sort {
  col: string;
  dir: 'asc' | 'desc';
}

/**
 * What the aggregate row computes under a column.
 *
 * Deliberately the vocabulary of the formula language where they overlap —
 * `SUM` in a rollup and Sum in the footer are the same word for the same
 * arithmetic, and nobody should have to learn it twice. The counting functions
 * have no formula equivalent, so they are named for what they count.
 */
export const AGG_FNS = ['sum', 'avg', 'min', 'max', 'filled', 'empty', 'unique', 'checked'] as const;

export type AggFn = (typeof AGG_FNS)[number];

/** Column name → the function shown beneath it. Absent means no summary. */
export type Summaries = Record<string, AggFn>;

export interface ViewDef {
  id: string;
  table: string;
  name: string;
  filters: Filter[];
  sort: Sort | null;
  /** The aggregate row this view carries, saved with it like its filters. */
  summaries: Summaries;
  /** Columns this view leaves out of its SELECT entirely. */
  hidden: string[];
  position: number;
  /** System views ship with the table and cannot be deleted. */
  system: boolean;
}

/** A view as the catalogue serves it: the definition plus a live row count. */
export interface ViewMeta extends ViewDef {
  /** Rows matching this view's filters; `null` if they no longer compile. */
  rowCount: number | null;
}

export type Role = 'admin' | 'editor' | 'viewer';

export const ROLE_RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2 };

export const ROLES: Role[] = ['viewer', 'editor', 'admin'];

/**
 * A person, globally. Accounts live in the control database and are shared
 * across every workspace they belong to — one email, one password, one session.
 * A role is never a property of an account; it belongs to a `Membership`.
 */
export interface Account {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  lastSeenAt: string | null;
  disabled: boolean;
  /**
   * Whether the address has been confirmed. Nothing is gated on it — it decides
   * only whether Wellformed can reach this person when they are locked out.
   */
  verified: boolean;
}

/** An account as seen from inside one workspace: the account plus its role there. */
export interface Member extends Account {
  role: Role;
  /** When this account joined this workspace. */
  joinedAt: string;
}

export type WorkspaceStatus = 'active' | 'suspended';

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'cancelled';

/** What a workspace is paying for. Always about the workspace, never the account. */
export interface Subscription {
  plan: PlanId;
  status: SubscriptionStatus;
  /** When a trial of the paid plan runs out, if one is running. */
  trialEndsAt: string | null;
  /** End of the period already paid for. */
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Who is taking the money, or null on an install with no billing. */
  provider: string | null;
}

/** What the billing screen shows: the subscription, and what it is being used for. */
export interface BillingSummary {
  subscription: Subscription;
  /** The plan whose limits apply right now — a live trial is the paid plan. */
  effectivePlan: PlanId;
  usage: { rows: number; tables: number };
  /** Whether this install can take money at all. */
  checkoutAvailable: boolean;
  /**
   * Whether `effectivePlan` is a ceiling here, or only a record of what the
   * workspace is nominally on. False on an install under its own licence.
   */
  metered: boolean;
  /**
   * Who this install is licensed to, when it is licensed at all.
   *
   * `Acme B.V. · WF-2026-001 · since 3 March 2026 · perpetual`, read out of the
   * signed licence rather than typed anywhere — which is why it is worth
   * showing: it is one of the few lines in the product nobody on this side of
   * it could have written.
   *
   * Answered to admins only. It names a customer of the copyright holder, and
   * that is not a fact for the unauthenticated config.
   */
  licensedTo: string | null;
  /**
   * Where this workspace's invoices go. Belongs to the workspace, not to any
   * admin, so it survives everyone who set it up leaving. Null until someone
   * has said — checkout asks rather than guessing.
   */
  billingEmail: string | null;
}

/** A workspace as seen by one account — the role is that account's, not the workspace's. */
export interface Workspace {
  id: string;
  /** URL segment: `/w/<slug>`. Lowercase, stable, unique across the install. */
  slug: string;
  name: string;
  role: Role;
  status: WorkspaceStatus;
  createdAt: string;
  /**
   * ISO 4217 code every currency column in this workspace reads in. Here
   * rather than on the column because a business keeps one set of books, and
   * here rather than fetched separately because every grid needs it to draw
   * its first cell.
   */
  currency: string;
  /**
   * The language this workspace speaks — the chrome around the grid, and how
   * numbers and dates are written. On the workspace rather than the account
   * for the same reason the currency is: the tables, columns and views in here
   * carry names in one language, and a Dutch grid inside English chrome reads
   * like two products taped together. See `locale.ts`.
   */
  locale: Locale;
  /** Carried on the bootstrap so the shell can warn without a second call. */
  plan: PlanId;
  billingStatus: SubscriptionStatus;
  trialEndsAt: string | null;
}

/** What an invite link reveals before it is accepted — deliberately little. */
export interface InviteInfo {
  workspaceName: string;
  workspaceSlug: string;
  email: string;
  role: Role;
  expiresAt: string;
  /** True when no account exists for `email` yet, so accepting must create one. */
  needsAccount: boolean;
}

/** A pending invite, as listed to a workspace admin. */
export interface PendingInvite {
  id: string;
  email: string;
  role: Role;
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string;
  /**
   * Only returned at creation — the token is stored hashed and cannot be shown
   * again. A path, not a URL: the client makes it absolute against its own
   * origin, so Wellformed needs no configured public hostname.
   */
  acceptPath?: string;
}

/**
 * What kind of file an import came out of.
 *
 * `delimited` covers everything separated by a character — CSV, TSV, and the
 * semicolon-separated files half of Europe calls CSV. The other two are ZIP
 * archives of XML and are read as workbooks, sheets and all.
 */
export type ImportFormat = 'delimited' | 'xlsx' | 'ods';

/**
 * The types an import can write directly. The rest — select, link, unique —
 * need the tightening dry run, because they are claims about the whole column
 * that deserve a report, not a checkbox in a file dialog.
 */
export const IMPORT_COLUMN_TYPES = ['text', 'number', 'currency', 'date', 'checkbox'] as const;

export type ImportColumnType = (typeof IMPORT_COLUMN_TYPES)[number];

/**
 * One correction to a guessed column, addressed by its position in the
 * preview. Position rather than name, because the name is exactly the thing
 * being corrected.
 */
export interface ImportColumnOverride {
  index: number;
  /** What the column should be called; the identifier is derived from it. */
  label?: string;
  type?: ImportColumnType;
}

/**
 * One correction to a group heading, addressed by its position in the
 * preview's `groups`. It renames every column in the group at once — that is
 * the point: one messy merged cell should be one correction, not one per
 * column. Corrected to nothing, the group stops prefixing its columns.
 */
export interface ImportGroupOverride {
  index: number;
  label: string;
}

/** One column of an incoming spreadsheet, ready to become or match a column. */
export interface ImportColumn {
  /** The derived SQL identifier — or the existing column's, when appending. */
  name: string;
  /** What the column will be called, taken from the file unless overridden. */
  label: string;
  /** What the file called it, so a correction can say what it corrected. */
  header: string;
  /** The group heading this column sits under, or null outside any group. */
  group: string | null;
  type: ColumnType;
  /**
   * Values that will not read as the chosen type. Zero when the type was
   * inferred — a guess is only made when every value fits it — so anything
   * here is the answer to somebody's override, shown before it can refuse.
   */
  unreadable: number;
  /** The first value that would not read, so the count points at something. */
  problem: string | null;
}

/**
 * The junk an import walked past, counted.
 *
 * Reported rather than silently swallowed: the parser guesses where the header
 * is and what an empty column is, and a guess nobody is shown is a guess nobody
 * can correct.
 */
export interface ImportNotes {
  /** Rows above the header — titles, export stamps, blank lines. */
  preamble: number;
  /** Rows under the header with nothing in them, anywhere in the file. */
  blankRows: number;
  /** Nameless columns with no values, usually trailing separators. */
  emptyColumns: number;
  /** Trailing rows left out for being totals rather than data. */
  footer: number;
}

export interface ImportPreview {
  columns: ImportColumn[];
  format: ImportFormat;
  /** What cut the text into columns; `null` when a workbook did. */
  delimiter: string | null;
  /** The sheet these rows came from, and `null` for delimited text. */
  sheet: string | null;
  /** Every sheet in the workbook, in tab order — another guess to correct. */
  sheets: string[];
  rowCount: number;
  /** Rows past the per-import cap, which will not be written. */
  skipped: number;
  ignored: ImportNotes;
  /** The row the column names came from — a guess until someone corrects it. */
  headerRow: number;
  /**
   * The row folded in front of the column names — the file's merged group
   * headings, "Q1" over Omzet and Kosten — or null when none is in use.
   */
  groupRow: number | null;
  /** A row that could serve as the group row but is not being used. */
  groupCandidate: number | null;
  /**
   * The group headings in use, in file order, each with the number of
   * columns under it — what the group editor in the preview edits.
   */
  groups: { label: string; count: number }[];
  /** Trailing rows left out for being totals rather than data. */
  footerRows: number;
  /** How many the parser would leave out on its own, in use or not. */
  footerCandidate: number;
  /** The rows the footer guess points at, rendered for display. */
  footerText: string[];
  /** The opening rows, one line each, to pick another header row from. */
  firstRows: string[];
  sample: Record<string, string | number | boolean | null>[];
}

export interface ImportResult {
  table: string;
  imported: number;
  skipped: number;
  ignored: ImportNotes;
  columns: ImportColumn[];
  /** The per-import row cap, so the client can explain a `skipped` count. */
  limit: number;
  /**
   * Whether this import took the workspace past what its plan allows.
   *
   * An import is let through whole even when it will not fit, so this is the
   * moment to say so. Silence here would mean finding out later, from a
   * refusal to add one row, why a workspace that imported cleanly is stuck.
   */
  overLimit: boolean;
}

/**
 * What the client can know about this install before signing in — all of it
 * operator configuration, none of it about any workspace or account.
 */
export interface PublicConfig {
  signupsOpen: boolean;
  /** Whether "we sent you a link" is a true sentence on this install. */
  emailEnabled: boolean;
  /**
   * Whether the plans mean anything here.
   *
   * False on an install running under its own licence: there is no ceiling to
   * warn about and no checkout to send anyone to, so a price list and an
   * upgrade button are both advertisements for something nobody here can buy.
   */
  metered: boolean;
  /**
   * The workspaces anyone may read without an account — usually one per
   * language. All of them, rather than one picked here: which demo to link
   * is the page's decision, because only the page knows what language it is
   * speaking, and every published slug has to be recognisable as a demo no
   * matter which language brought the visitor in. Currency and language
   * travel with each one: a visitor has no membership to read them from, and
   * a demo of a euro business that prints dollars sells the wrong thing.
   */
  demos: Array<{ slug: string; name: string; currency: string; locale: Locale }>;
}

/** Everything `/api/auth/me` answers: who you are and where you can go. */
export interface SessionResponse {
  account: Account | null;
  workspaces: Workspace[];
}

export type AuditKind = 'edit' | 'insert' | 'delete' | 'schema' | 'workflow' | 'view' | 'auth';

export interface AuditEntry {
  id: number;
  ts: string;
  who: string;
  kind: AuditKind;
  table: string | null;
  rowId: string | null;
  rowLabel: string | null;
  column: string | null;
  old: string | null;
  nw: string | null;
  /**
   * The agent key this change came through, by its label, or null for a person
   * working in the browser.
   *
   * `who` stays the account either way. An assistant acts as you, with your
   * role, on a key you made and can revoke — so you are accountable for what it
   * does. This field is the disclosure, not a second identity.
   */
  via: string | null;
}

/**
 * An agent key: the credential an AI assistant uses to reach one workspace.
 *
 * One key belongs to one account in one workspace. Its `ceiling` caps what it
 * can do; the role that actually applies is the lower of the ceiling and the
 * account's own role there, resolved per request. So a key cannot outlive its
 * holder's access — demote the account and every key it made drops with it.
 */
export interface AgentKey {
  id: string;
  label: string;
  ceiling: Role;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  /** Whose key it is. Present so an admin can tell keys in a shared workspace apart. */
  accountId: string;
  accountName: string;
  /**
   * Returned once, at creation. Only its hash is stored, so there is no second
   * chance to read it — the same rule as invites and reset links.
   */
  secret?: string;
}

/** A workflow rule fires when `column` is set to `equals` on `table`. */
export interface RuleAction {
  type: 'set';
  column: string;
  /** Literal, or one of the tokens `@today`, `@now`, `@user`, `@empty`. */
  value: string;
}

export interface Rule {
  id: string;
  table: string;
  column: string;
  equals: string;
  label: string;
  actions: RuleAction[];
  enabled: boolean;
}

/** Syntax-highlight token for the live SQL bar. `var` marks the artificial
 *  variables the display layer folds computed columns into. */
export interface SqlToken {
  c: 'kw' | 'id' | 'str' | 'num' | 'fn' | 'op' | 'var' | 'plain';
  s: string;
}

export type CellValue = string | number | boolean | null;

export interface Row {
  id: string;
  /** Column name → value. Link columns carry the target row id. */
  values: Record<string, CellValue>;
  /** Link column name → display label of the linked row. */
  labels: Record<string, string | null>;
}

export interface RowsResponse {
  rows: Row[];
  total: number;
  limit: number;
  offset: number;
  /** The statement the server actually ran, with literals inlined for display. */
  sql: string;
  tokens: SqlToken[];
  /** Columns whose expressions failed to compile; their cells return null. */
  columnErrors: Record<string, string>;
}

/**
 * The aggregate row, computed over every row the view matches — not over the
 * page the grid happens to have loaded. A total that changed as you scrolled
 * would be a number nobody could act on.
 */
export interface SummaryResponse {
  /** Rows the aggregates were taken over; the same total the grid pages through. */
  total: number;
  /** Column name → its aggregate. Null where there was nothing to aggregate. */
  values: Record<string, CellValue>;
  /** Columns whose summary could not be computed, and why. */
  errors: Record<string, string>;
  /** The statement the server actually ran, with literals inlined for display. */
  sql: string;
  tokens: SqlToken[];
}

export interface MetaResponse {
  tables: TableMeta[];
  views: ViewMeta[];
  rules: Rule[];
}

export interface ApiError {
  error: string;
  message: string;
  /** Present on validation failures. */
  details?: unknown;
}
